

import React, { forwardRef, useRef, useState, useEffect } from "react";

const CustomModel = forwardRef(
  ({ show, children, heading, handleCancel, width, maxwidth, minwidth, isVisible }, ref) => {
    const modalRef = useRef(null);
    const headerRef = useRef(null);
    const [position, setPosition] = useState({ x: 100, y: 100 });
    const draggingRef = useRef(false);
    const offsetRef = useRef({ x: 0, y: 0 });

    const onPointerDown = (e) => {
      if (["INPUT", "TEXTAREA", "BUTTON", "SELECT", "LABEL"].includes(e.target.tagName)) return;
      const modal = modalRef.current;
      if (!modal) return;

      // Capture pointer for smoother drag
      headerRef.current?.setPointerCapture?.(e.pointerId);

      // Use current position, not bounding rect
      offsetRef.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
      draggingRef.current = true;
    };

    useEffect(() => {
      const onPointerMove = (e) => {
        if (!draggingRef.current) return;
        e.preventDefault();

        const modal = modalRef.current;
        if (!modal) return;

        let newX = e.clientX - offsetRef.current.x;
        let newY = e.clientY - offsetRef.current.y;

        const modalWidth = modal.offsetWidth;
        const modalHeight = modal.offsetHeight;

        // Clamp inside window
        newX = Math.max(0, Math.min(newX, window.innerWidth - modalWidth));
        newY = Math.max(0, Math.min(newY, window.innerHeight - modalHeight));

        setPosition({ x: newX, y: newY });
      };

      const onPointerUp = (e) => {
        if (!draggingRef.current) return;
        headerRef.current?.releasePointerCapture?.(e.pointerId);
        draggingRef.current = false;
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
      document.addEventListener("pointercancel", onPointerUp);

      return () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        document.removeEventListener("pointercancel", onPointerUp);
      };
    }, [position]);

    return (
      <div
        ref={modalRef}
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          backgroundColor: "#fff",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          padding: "16px",
          zIndex: 1000,
          width: width || "30%",
          maxWidth: maxwidth || width,
          minWidth: minwidth,
          borderRadius: "16px",
        }}
      >
        <div className="flex flex-col">
          <div className="w-full p-2 border-b-[1px] border-grey">
            <div
              ref={(node) => {
                headerRef.current = node;
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
              }}
              onPointerDown={onPointerDown}
              className="w-full flex justify-between"
              style={{ cursor: "move", userSelect: draggingRef.current ? "none" : "auto" }}
            >
              <span className="text-[16px] font-semibold">{heading}</span>
              <button
                onClick={() => handleCancel?.()}
                className="font-semibold py-1 px-2 border-black rounded-[8px] shadow-md"
              >
                x
              </button>
            </div>
          </div>
          <div>{children}</div>
        </div>
      </div>
    );
  }
);

export default CustomModel;
